import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FiX, FiArrowLeft, FiMail } from 'react-icons/fi';
import useAuth from '../../hooks/useAuth';
import useScrollLock from '../../hooks/useScrollLock';
import { isNativeApp } from '../../utils/platform';
import { businessPostAuthPath } from '../../utils/businessMode';
import { ensureGoogleInitialized, renderGoogleButton } from '../../utils/googleSignIn';
import { APPLE_SIGNIN_ENABLED } from '../../utils/nativeSocialLogin';
import { APPLE_WEB_SIGNIN_ENABLED } from '../../utils/appleSignIn';
import NativeGoogleButton from './NativeGoogleButton';
import AppleSignInButton from './AppleSignInButton';
import TextField from '../ui/TextField';
import PasswordField from '../ui/PasswordField';
import Button from '../ui/Button';

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';

// Focus ring matched to the design-system Button primitive, for the hand-rolled
// buttons in here (keyboard a11y — WCAG 2.4.7).
const FOCUS_RING = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-offset-2';

// ── Global login drawer ──────────────────────────────────────────────────────
// A Fresha-style bottom sheet (centered modal on desktop) for guests, opened
// from the Profil tab + the empty-state "Conectează-te" CTAs instead of bouncing
// to the standalone /login page. It reuses the SAME auth flow as LoginPage
// (useAuth.login / loginWithGoogle / loginWithApple), so there's no second auth
// system — just a lighter entry point. Email step is email + password (Petzy
// isn't passwordless).

const LoginDrawerContext = createContext(null);

export function useLoginDrawer() {
  // No-op fallback if used outside the provider, so a stray caller never crashes.
  return useContext(LoginDrawerContext) || { open: () => {}, close: () => {}, isOpen: false };
}

export function LoginDrawerProvider({ children }) {
  const [state, setState] = useState({ open: false, redirectTo: null });
  // redirectTo: where to send the user AFTER a successful login (e.g. the Profil
  // tab opens the drawer over the current screen, then lands on /profile).
  const open = useCallback((opts = {}) => setState({ open: true, redirectTo: opts.redirectTo || null }), []);
  const close = useCallback(() => setState((s) => ({ ...s, open: false })), []);
  return (
    <LoginDrawerContext.Provider value={{ open, close, isOpen: state.open }}>
      {children}
      {state.open && <LoginDrawer redirectTo={state.redirectTo} onClose={close} />}
    </LoginDrawerContext.Provider>
  );
}

function LoginDrawer({ redirectTo, onClose }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, loginWithGoogle, loginWithApple, user } = useAuth();
  const [view, setView] = useState('main'); // 'main' (social + email choice) | 'email'
  const [form, setForm] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const googleBtnRef = useRef(null);
  const panelRef = useRef(null);

  // Lock the page behind the sheet (handles the iOS/WKWebView touch-scroll case
  // + native keyboard scroll). The drawer only mounts when open, so always-on.
  useScrollLock(true);

  // Move focus into the sheet on open and restore it to the trigger on close,
  // so keyboard / screen-reader users land inside the modal.
  useEffect(() => {
    const prevFocused = document.activeElement;
    panelRef.current?.focus();
    return () => { try { prevFocused?.focus?.(); } catch { /* element gone */ } };
  }, []);

  // Esc closes + Tab traps focus inside the sheet (web a11y, WCAG 2.1.1/2.4.3):
  // keyboard users shouldn't be able to tab out into the page behind the modal.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key !== 'Tab' || !panelRef.current) return;
      const nodes = panelRef.current.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      // Skip hidden controls (e.g. the other view's fields). The GIS Google
      // button is a cross-origin iframe, so it isn't enumerable here — an
      // accepted focus-trap limitation, not a regression.
      const focusables = Array.prototype.filter.call(nodes, (el) => el.offsetParent !== null);
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      // On open, focus sits on the panel itself (tabIndex=-1), which is NOT a
      // focusable — so Shift+Tab from there would escape the modal (the panel has
      // no prior tabbable inside it). Treat "focus not on a real focusable" (the
      // panel, or anything outside) as an edge: pull it to last on Shift+Tab,
      // first on Tab. The normal wrap handles focus already on first/last.
      const onFocusable = panelRef.current.contains(document.activeElement)
        && document.activeElement !== panelRef.current;
      if (!onFocusable) { (e.shiftKey ? last : first).focus(); e.preventDefault(); }
      else if (e.shiftKey && document.activeElement === first) { last.focus(); e.preventDefault(); }
      else if (!e.shiftKey && document.activeElement === last) { first.focus(); e.preventDefault(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Safety: if the user becomes authenticated by any path, close.
  useEffect(() => { if (user) onClose(); }, [user, onClose]);

  const finish = useCallback((role) => {
    onClose();
    // Business build fully overrides the native/web routing below: owner &
    // specialist → dashboard, admin → admin, CLIENT → reject screen
    // (self-logs-out). Returns null off business mode, so the drawer's normal
    // native/web/client routing falls through untouched.
    const businessPath = businessPostAuthPath(role);
    if (businessPath) {
      navigate(businessPath);
      return;
    }
    // Where to land after login:
    // - Native: HOME, matching LoginPage's convention (tapping a tab while
    //   logged out → login → home, not straight into the account page).
    // - Web B2B (salon owner / admin): the role dashboard.
    // - Web client: the tab they came from (redirectTo).
    // If the destination is where we already are, skip the nav and let the page
    // re-render in its logged-in form.
    let dest;
    if (isNativeApp) dest = '/';
    else if (role === 'SALON_OWNER' || role === 'SPECIALIST') dest = '/salon/dashboard';
    else if (role === 'ADMIN') dest = '/admin';
    else dest = redirectTo;
    if (dest && dest !== location.pathname) navigate(dest);
  }, [onClose, redirectTo, navigate, location.pathname]);

  const onGoogle = useCallback(async (credential) => {
    if (!credential) return;
    setLoading(true); setError('');
    try { const u = await loginWithGoogle(credential); finish(u?.role); }
    catch { setError('Autentificarea cu Google a eșuat. Încearcă din nou.'); }
    finally { setLoading(false); }
  }, [loginWithGoogle, finish]);

  const onApple = useCallback(async ({ identityToken, fullName, authorizationCode }) => {
    setLoading(true); setError('');
    try { const u = await loginWithApple(identityToken, fullName, undefined, authorizationCode); finish(u?.role); }
    catch { setError('Autentificarea cu Apple a eșuat. Încearcă din nou.'); }
    finally { setLoading(false); }
  }, [loginWithApple, finish]);

  // Web only: render the Google Identity Services button (native uses the
  // Capacitor plugin via NativeGoogleButton). Mirrors LoginPage's init.
  useEffect(() => {
    if (isNativeApp || view !== 'main') return undefined;
    const tryInit = () => {
      // Ref may not be mounted yet on the first tick (rapid view toggles / the
      // mount animation) — renderGoogleButton does container.innerHTML = '' and
      // would throw on null. Bail and let the interval retry next tick.
      if (!googleBtnRef.current) return false;
      if (!ensureGoogleInitialized(GOOGLE_CLIENT_ID, (resp) => onGoogle(resp.credential))) return false;
      renderGoogleButton(googleBtnRef.current, {
        type: 'standard', theme: 'outline', size: 'large',
        text: 'continue_with', width: googleBtnRef.current.offsetWidth || 320, locale: 'ro',
      });
      return true;
    };
    if (tryInit()) return undefined;
    const iv = setInterval(() => { if (tryInit()) clearInterval(iv); }, 200);
    return () => clearInterval(iv);
  }, [view, onGoogle]);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const u = await login(form.email, form.password);
      finish(u?.role);
    } catch (err) {
      if (err.response?.data?.requiresVerification) {
        onClose();
        navigate('/verify-email', { state: { email: form.email, from: redirectTo || undefined } });
      } else {
        setError(err.response?.data?.message || 'Email sau parolă incorectă');
      }
    } finally { setLoading(false); }
  };

  const goRegister = () => { onClose(); navigate('/register', { state: redirectTo ? { from: redirectTo } : undefined }); };
  const goForgot = () => { onClose(); navigate('/forgot-password'); };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-label="Conectează-te sau înregistrează-te"
    >
      <div className="absolute inset-0 bg-ink/50 backdrop-blur-sm" onClick={onClose} />

      {/* Content-height sheet anchored to the bottom (items-end) so the social /
          email buttons sit low — within easy one-handed thumb reach. A full-
          height sheet pushed them to the top of the screen, which was hard to
          reach. Caps at 90dvh — a DYNAMIC viewport unit that tracks the mobile
          address-bar / keyboard resize (plain vh is the largest viewport, so it
          can overflow the visible area); scrolls if the form + keyboard need it.
          vh→dvh fallback (matches the index.css convention): old browsers without
          dvh (iOS < 15.4 / Chrome < 108) take the 90vh cap, modern ones override
          to 90dvh via @supports — without it those browsers would drop the cap
          entirely (unbounded sheet, no internal scroll). */}
      <div
        ref={panelRef}
        tabIndex={-1}
        className="relative max-h-[90vh] supports-[height:1dvh]:max-h-[90dvh] w-full max-w-md overflow-y-auto rounded-t-3xl bg-cream-50 px-6 pb-[calc(env(safe-area-inset-bottom,0px)+1.5rem)] pt-6 shadow-2xl outline-none sm:rounded-3xl sm:pb-8"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Închide"
          className={`absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full text-ink-500 transition active:bg-ink/5 ${FOCUS_RING}`}
        >
          <FiX className="h-6 w-6" />
        </button>
        {view === 'email' && (
          <button
            type="button"
            onClick={() => { setView('main'); setError(''); }}
            aria-label="Înapoi"
            className={`absolute left-3 top-3 flex h-9 w-9 items-center justify-center rounded-full text-ink-500 transition active:bg-ink/5 ${FOCUS_RING}`}
          >
            <FiArrowLeft className="h-6 w-6" />
          </button>
        )}

        <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-ink">Conectează-te sau înregistrează-te</h2>
        <p className="mb-6 mt-1 text-sm text-ink-500">Ai nevoie de cont ca să continui.</p>

        {error && (
          <div role="alert" className="mb-4 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
        )}

        {view === 'main' ? (
          <div className="space-y-3">
            {/* Apple above Google (App Store 4.8 order, kept on web for
                consistency). Native gate = the Xcode capability; web gate = the
                Services ID being configured. */}
            {(isNativeApp ? APPLE_SIGNIN_ENABLED : APPLE_WEB_SIGNIN_ENABLED) && (
              <AppleSignInButton
                onAuthenticate={onApple}
                onError={() => setError('Autentificarea cu Apple a eșuat. Încearcă din nou.')}
                disabled={loading}
              />
            )}
            {isNativeApp ? (
              <NativeGoogleButton onCredential={onGoogle} onError={(m) => setError(`Google: ${m}`)} disabled={loading} />
            ) : (
              <div ref={googleBtnRef} className="flex w-full justify-center" />
            )}
            <button
              type="button"
              onClick={() => { setView('email'); setError(''); }}
              disabled={loading}
              className={`flex w-full items-center justify-center gap-3 rounded-xl border border-ink/15 bg-cream-50 px-4 py-3 text-sm font-semibold text-ink transition active:bg-cream-100 disabled:opacity-60 ${FOCUS_RING}`}
            >
              <FiMail className="h-5 w-5" />
              Continuă cu email
            </button>
          </div>
        ) : (
          <form onSubmit={handleEmailSubmit} onChange={() => { if (error) setError(''); }} className="space-y-3">
            <TextField
              id="drawer-email"
              label="Adresă email"
              type="email"
              required
              autoComplete="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              floating
            />
            <PasswordField
              id="drawer-password"
              label="Parolă"
              required
              autoComplete="current-password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              floating
            />
            <div className="flex justify-end">
              <button type="button" onClick={goForgot} className={`rounded text-xs font-semibold text-ink-600 transition hover:underline ${FOCUS_RING}`}>
                Am uitat parola
              </button>
            </div>
            <Button type="submit" disabled={loading} className="mt-1 w-full">
              {loading ? 'Se conectează…' : 'Conectare'}
            </Button>
          </form>
        )}

        <p className="mt-6 text-center text-sm text-ink-500">
          Nu ai cont?{' '}
          <button type="button" onClick={goRegister} className={`rounded font-semibold text-primary-600 transition hover:underline ${FOCUS_RING}`}>
            Creează unul gratuit
          </button>
        </p>
      </div>
    </div>
  );
}
